import { useEffect, useState } from 'react';
import { apiFetch } from '../../pages/marketplace/api';
import { CalendarPlus, Loader, X } from 'lucide-react';

/**
 * A practitioner putting a session in the diary herself.
 *
 * Every booking used to start with the client, which left no way to run an introductory
 * package where only the first session is chosen by the client, and no way to write up a
 * session arranged over the phone (owner, Jun 2026).
 *
 * Her choice twice over: place it outright or propose it for them to confirm, and settle it
 * with a package credit, with their card when they confirm, or between the two of them.
 */
const field = {
  width: '100%', padding: '8px 10px', borderRadius: 8, fontSize: 13,
  border: '1px solid rgba(0,0,0,0.15)', background: '#fff', color: '#1a1a16',
  fontFamily: 'inherit',
};
const labelStyle = { fontSize: 11, fontWeight: 600, color: '#5a5a54', display: 'block',
                     marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em' };

export default function PlaceSessionPanel({ onPlaced, open: openProp, onOpenChange, prefillDate }) {
  const [openSelf, setOpenSelf] = useState(false);
  // Opened from the calendar now as well as from its own button, so it answers to either.
  const controlled = openProp !== undefined;
  const open = controlled ? openProp : openSelf;
  const setOpen = (v) => (controlled ? onOpenChange(v) : setOpenSelf(v));
  const [sessionTypes, setSessionTypes] = useState([]);
  const [credits, setCredits] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [expiredAsk, setExpiredAsk] = useState(false);
  const [form, setForm] = useState({
    session_type_id: '', scheduled_at: '', client_email: '', client_name: '',
    mode: 'propose', settle: 'client', package_id: '', note: '', expires_days: 7,
  });

  useEffect(() => {
    if (!open) return;
    apiFetch('/practitioner/booking-profile')
      .then((p) => setSessionTypes(p?.session_types || []))
      .catch(() => {});
  }, [open]);

  // Which credits this person holds — expired ones shown too, because she may allow them.
  useEffect(() => {
    const email = form.client_email.trim();
    if (!open || !email.includes('@')) { setCredits([]); return; }
    const id = setTimeout(() => {
      apiFetch(`/practitioner/client-credits?email=${encodeURIComponent(email)}`)
        .then((d) => setCredits(d?.credits || []))
        .catch(() => setCredits([]));
    }, 500);
    return () => clearTimeout(id);
  }, [form.client_email, open]);

  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  // A day picked on the calendar arrives as the date, with a workable hour she can change.
  useEffect(() => {
    if (!open || !prefillDate) return;
    setForm((prev) => (prev.scheduled_at.startsWith(prefillDate)
      ? prev
      : { ...prev, scheduled_at: `${prefillDate}T09:00` }));
  }, [open, prefillDate]);

  const submit = async (allowExpired = false) => {
    setError('');
    if (!form.session_type_id) return setError('Which session is it?');
    if (!form.scheduled_at) return setError('Pick a date and time.');
    if (!form.client_email.includes('@')) return setError("The client's email address, please.");
    setBusy(true);
    try {
      const body = {
        ...form,
        scheduled_at: new Date(form.scheduled_at).toISOString(),
        package_id: form.settle === 'credit' ? (form.package_id || null) : null,
        use_expired_credit: allowExpired,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };
      const placed = await apiFetch('/practitioner/bookings/place', {
        method: 'POST', body: JSON.stringify(body),
      });
      setOpen(false);
      setExpiredAsk(false);
      setForm({ session_type_id: '', scheduled_at: '', client_email: '', client_name: '',
                mode: 'propose', settle: 'client', package_id: '', note: '', expires_days: 7 });
      onPlaced(placed?.client_not_emailed
        ? 'Session created — but the email to your client did not go out. Check their address.'
        : form.mode === 'place'
          ? 'In the diary — your client has been told.'
          : 'Sent — your client has a link to confirm it.',
        placed?.proposal_link || '');
    } catch (e) {
      const detail = e?.message || 'Could not place that session';
      if (detail.includes('still let them use it')) { setExpiredAsk(true); setError(detail); }
      else setError(detail);
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    if (controlled) return null;
    return (
      <button className="vb-btn vb-btn-primary" onClick={() => setOpen(true)}
        data-testid="place-session-open">
        <CalendarPlus size={14} /> Schedule for a client
      </button>
    );
  }

  const usable = credits.filter((c) => !form.session_type_id
    || c.session_type_id === form.session_type_id);

  return (
    <div style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.1)', borderRadius: 12,
                  padding: 16, marginBottom: 16, maxWidth: 620 }}
      data-testid="place-session-panel">
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
        <h3 style={{ fontFamily: "'Literata', serif", fontSize: 17, margin: 0, flex: 1,
                     color: '#1a1a16' }}>
          Schedule a session for a client
        </h3>
        <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none',
          cursor: 'pointer', color: '#8a8a80' }} data-testid="place-session-close">
          <X size={16} />
        </button>
      </div>

      <div style={{ display: 'grid', gap: 12, gridTemplateColumns: '1fr 1fr' }}>
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={labelStyle}>Session</label>
          <select style={field} value={form.session_type_id}
            onChange={(e) => set({ session_type_id: e.target.value })}
            data-testid="place-session-type">
            <option value="">Choose one of your sessions…</option>
            {sessionTypes.filter((st) => !st.external_url).map((st) => (
              <option key={st.id} value={st.id}>
                {st.name}{st.price > 0 ? ` — $${st.price}` : ' — free'}
                {st.asleep ? ' (asleep)' : ''}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label style={labelStyle}>Date and time</label>
          <input type="datetime-local" style={field} value={form.scheduled_at}
            onChange={(e) => set({ scheduled_at: e.target.value })}
            data-testid="place-session-when" />
        </div>
        <div>
          <label style={labelStyle}>Their name</label>
          <input style={field} value={form.client_name} placeholder="Optional"
            onChange={(e) => set({ client_name: e.target.value })}
            data-testid="place-session-client-name" />
        </div>

        <div style={{ gridColumn: '1 / -1' }}>
          <label style={labelStyle}>Their email</label>
          <input style={field} value={form.client_email} placeholder="client@example.com"
            onChange={(e) => set({ client_email: e.target.value })}
            data-testid="place-session-client-email" />
        </div>

        <div>
          <label style={labelStyle}>How it goes out</label>
          <select style={field} value={form.mode} onChange={(e) => set({ mode: e.target.value })}
            data-testid="place-session-mode">
            <option value="propose">Ask them to confirm it</option>
            <option value="place">Just book it and tell them</option>
          </select>
        </div>
        <div>
          <label style={labelStyle}>Payment</label>
          <select style={field} value={form.settle}
            onChange={(e) => set({ settle: e.target.value })}
            data-testid="place-session-settle">
            <option value="client">They pay when they confirm</option>
            <option value="credit">Use one of their package credits</option>
            <option value="outside">Settled between us — nothing taken here</option>
          </select>
        </div>

        {form.settle === 'credit' && (
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={labelStyle}>Which credit</label>
            {usable.length === 0 ? (
              <p style={{ fontSize: 12, color: '#a3372f', margin: 0 }}
                data-testid="place-session-no-credits">
                No credits for this session on that address. They buy a package from your booking
                page — or choose another way to settle it.
              </p>
            ) : (
              <select style={field} value={form.package_id}
                onChange={(e) => set({ package_id: e.target.value })}
                data-testid="place-session-credit">
                <option value="">Choose a package…</option>
                {usable.map((c) => (
                  <option key={c.package_id} value={c.package_id}>
                    {c.session_type_name || 'Package'} — {c.remaining} left
                    {c.expired ? ' (expired)' : ''}
                  </option>
                ))}
              </select>
            )}
          </div>
        )}

        {form.mode === 'propose' && (
          <div>
            <label style={labelStyle}>They have</label>
            <select style={field} value={form.expires_days}
              onChange={(e) => set({ expires_days: parseInt(e.target.value, 10) })}
              data-testid="place-session-expiry">
              {[2, 3, 5, 7, 14, 30].map((d) => (
                <option key={d} value={d}>{d} days to answer</option>
              ))}
            </select>
          </div>
        )}

        <div style={{ gridColumn: '1 / -1' }}>
          <label style={labelStyle}>A note for them (optional)</label>
          <textarea style={{ ...field, minHeight: 64, resize: 'vertical' }} value={form.note}
            placeholder="This is the second of your three introductory sessions…"
            onChange={(e) => set({ note: e.target.value })}
            data-testid="place-session-note" />
        </div>
      </div>

      {error && (
        <p style={{ fontSize: 12, color: '#a3372f', background: 'rgba(163,55,47,0.07)',
                    padding: '8px 10px', borderRadius: 6, marginTop: 12 }}
          data-testid="place-session-error">
          {error}
        </p>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
        {expiredAsk ? (
          <>
            <button className="vb-btn vb-btn-primary" disabled={busy}
              onClick={() => submit(true)} data-testid="place-session-allow-expired">
              {busy ? <Loader size={14} /> : null} Let them use the expired credit
            </button>
            <button className="vb-btn" onClick={() => { setExpiredAsk(false); setError(''); }}
              data-testid="place-session-cancel-expired">
              Choose another way
            </button>
          </>
        ) : (
          <>
            <button className="vb-btn vb-btn-primary" disabled={busy}
              onClick={() => submit(false)} data-testid="place-session-submit">
              {busy ? <Loader size={14} /> : <CalendarPlus size={14} />}
              {form.mode === 'place' ? 'Book it' : 'Send it to them'}
            </button>
            <button className="vb-btn" onClick={() => setOpen(false)}
              data-testid="place-session-cancel">
              Cancel
            </button>
          </>
        )}
      </div>

      <p style={{ fontSize: 11, color: '#8a8a80', marginTop: 10, lineHeight: 1.5 }}>
        A session you propose holds the slot until they answer. If they don&apos;t, it frees
        itself and you can send it again from the card.
      </p>
    </div>
  );
}
